/**
 * Types for the `AskUserQuestion` agent tool.
 *
 * The agent emits an `AskUserQuestion` tool call (a normal `toolUse` SSE event)
 * and ends the turn — the tool has no backend `execute`. The UI renders a
 * question card, the user picks answer(s), and the UI posts the answer back as
 * a tool-result message to resume the turn.
 *
 * These shapes are part of the locked backend contract (pairs with backend
 * PR #1932). They are NOT in `backend-openapi.d.ts` because tool schemas aren't
 * part of the OpenAPI route spec.
 */

/** A single selectable option within a question. */
export interface AskUserQuestionOption {
  label: string
  description: string
}

/** A single question presented to the user. */
export interface AskUserQuestionItem {
  question: string
  header: string
  /** When true, the user may select multiple options; otherwise exactly one. */
  multiSelect: boolean
  /** 2-4 options. */
  options: Array<AskUserQuestionOption>
  /**
   * Set by the agent to attach an optional "why" box to THIS question, labelled
   * with this text. It annotates the pick rather than replacing it, which is
   * what separates it from the "Other" free-text row.
   */
  explanationPrompt?: string
}

/**
 * The tool input, parsed from `toolUse.input` (which arrives as a JSON string).
 * Contains 1-4 questions.
 */
export interface AskUserQuestionInput {
  /**
   * The agent's own explanation of why it needs these answers, rendered as the
   * header above the questions. Optional: older turns (and any agent that
   * omits it) fall back to the generic framing in `getQuestionFraming`.
   */
  intro?: string
  questions: Array<AskUserQuestionItem>
}

/** A single answered question, matched back to its input by `header`. */
export interface AskUserQuestionAnswerItem {
  header: string
  /**
   * The chosen option `label`s, plus the user's free-text value if they used
   * the "Other" choice. For non-multiSelect questions this has exactly one entry.
   */
  selected: Array<string>
  /**
   * The user's reasoning for `selected`, from this question's "why" box.
   * Omitted rather than empty when they skipped it.
   */
  explanation?: string
}

/**
 * The answer payload posted back as the tool-result content
 * (JSON.stringify'd). EVERY question in the input must have a corresponding
 * entry here, matched by `header`.
 */
export interface AskUserQuestionAnswer {
  answers: Array<AskUserQuestionAnswerItem>
  /**
   * Set when the user dismissed the card instead of answering. The run is
   * blocked either way, so it still resumes. This is what stops it treating an
   * empty `selected` list as a deliberate "none of these".
   */
  dismissed?: boolean
}

/**
 * The payload for a *dismissal*, meaning the user threw the card away rather
 * than answering it (HIG-6042).
 *
 * Still posts: this run is blocked on the card, and only the resume can tell it
 * to stop waiting. Dropping the card client-side would strand it for good,
 * which is worse than a run that continues knowing it was refused.
 *
 * Every question is present with nothing selected, mirroring
 * `buildFeedbackDismissal`, so the agent can tell "they refused" from "it was
 * never asked". `dismissed` is what separates this from an answer where the
 * user genuinely picked nothing.
 */
export function buildQuestionDismissal(questions: Array<AskUserQuestionItem>): AskUserQuestionAnswer {
  return { answers: questions.map((question) => ({ header: question.header, selected: [] })), dismissed: true }
}

/**
 * Parse a raw `toolInput` (stored parsed, but defend against the JSON-string
 * path) into an `AskUserQuestionInput`. Returns null unless the result is our
 * shape with a non-empty `questions` array, so callers can branch on a single
 * truthy check instead of repeating the parse/guard at every render site.
 */
export function parseAskUserQuestionInput(rawInput: unknown): AskUserQuestionInput | null {
  let parsed: AskUserQuestionInput | null = null
  try {
    parsed = (typeof rawInput === 'string' ? JSON.parse(rawInput) : rawInput) as AskUserQuestionInput
  } catch {
    return null
  }
  return parsed?.questions?.length ? parsed : null
}

/**
 * Parse a submitted answer payload (the tool-result content, or a staged
 * action's `outputData`) back into an `AskUserQuestionAnswer`. Returns null
 * unless it is our shape, so callers can treat "resolved" as one truthy check.
 */
export function parseAskUserQuestionAnswer(result: unknown): AskUserQuestionAnswer | null {
  if (!result) return null
  let parsed: unknown = result
  try {
    parsed = typeof result === 'string' ? JSON.parse(result) : result
  } catch {
    return null
  }
  return parsed && Array.isArray((parsed as AskUserQuestionAnswer).answers) ? (parsed as AskUserQuestionAnswer) : null
}

/**
 * Synthetic `toolSet`/`toolName` for the staged action that represents a
 * pending AskUserQuestion surfaced in the actions panel by a *background* run
 * (planner / staged-action agent). Mirrors the backend constants
 * (ASK_USER_QUESTION_STAGED_TOOLSET / _TOOLNAME). Foreground chats render the
 * question inline instead and never create one of these.
 */
export const ASK_USER_QUESTION_STAGED_TOOLSET = 'ask_user_question'
export const ASK_USER_QUESTION_STAGED_TOOLNAME = 'question'

/**
 * True when a staged action is a pending background AskUserQuestion (rather than
 * a normal write-tool draft). Used by the action drawer to render the
 * interactive question card and route the answer to the `/answer` endpoint.
 */
export function isAskUserQuestionStagedAction(action: { toolSet?: string; toolName?: string }): boolean {
  return action.toolSet === ASK_USER_QUESTION_STAGED_TOOLSET && action.toolName === ASK_USER_QUESTION_STAGED_TOOLNAME
}

/**
 * The framing sentence for a background question — *why* it exists, not the
 * question itself: which run paused and what answering does. Shared by the
 * panel cell preview and the drawer body so they can't drift. `references[0]`
 * carries the human title of what the run was working on ("Monday core sync",
 * "Chat Conversation", …).
 *
 * The agent's own `intro` wins when it set one: it can name what it's drafting
 * and what it can't decide, which this generic sentence never can. The fallback
 * stays for older questions and agents that omit it.
 */
export function getQuestionFraming(action: {
  references?: Array<{ title?: string }> | null
  inputData?: unknown
  outputData?: unknown
}): string {
  const answered =
    !!action.outputData &&
    typeof action.outputData === 'object' &&
    Array.isArray((action.outputData as { answers?: unknown }).answers)
  if (answered) return 'You answered this — Highlight continued with your answer.'
  const intro = parseAskUserQuestionInput(action.inputData)?.intro?.trim()
  if (intro) return intro
  const rawSourceTitle = action.references?.[0]?.title
  const sourceTitle = rawSourceTitle === 'Chat Conversation' ? 'a chat' : rawSourceTitle
  return `While working${sourceTitle ? ` on ${sourceTitle}` : ' in the background'}, Highlight hit a decision it needs you to make. Answering lets it pick up where it left off.`
}

/**
 * Monotonic per-open token. The chat drawer stays mounted (keyed by drawer id),
 * so reopening the *same* question — same conversation and message — only
 * refreshes its context with identical values. A fresh nonce on every call gives
 * the drawer a value that always changes, so it re-runs its scroll-to-message
 * even when the target id is unchanged.
 */
let scrollRequestCounter = 0

/**
 * The chat-drawer context for jumping straight to a background question in its
 * originating conversation, so the user can answer it inline with full context.
 * Returns null when the question has no chat to open (e.g. a non-chat background
 * run) — callers should fall back to the question drawer in that case. When the
 * authoring message is known we scroll to it; otherwise we land at the bottom,
 * where the question is the latest content. Call this once per open (from a
 * press handler), never during render — each call advances the scroll nonce.
 *
 * `questionActionId` rides along so the chat drawer can fall back to the
 * question drawer if the conversation turns out to be missing/unloadable —
 * conversationId being set doesn't guarantee the chat still exists.
 */
export function getQuestionChatTarget(action: {
  id?: string
  originConversationId?: string | null
  conversationId?: string | null
  triggeringLlmMessageId?: string | null
}): {
  chatId: string
  scrollToMessageId?: string
  scrollNonce?: number
  instantScrollToBottom?: boolean
  questionActionId?: string
} | null {
  const conversationId = action.originConversationId ?? action.conversationId
  if (!conversationId) return null
  const scrollNonce = ++scrollRequestCounter
  const base = { chatId: conversationId, scrollNonce, questionActionId: action.id }
  return action.conversationId === conversationId && action.triggeringLlmMessageId
    ? { ...base, scrollToMessageId: action.triggeringLlmMessageId }
    : { ...base, instantScrollToBottom: true }
}
